// Copyright (c) StrangeBee 2024

import { THTooltip } from '@Molecules/THTooltip/THTooltip';
import React, { ReactElement } from 'react';
import { useLocalMessage } from '@Helpers/';
import { ListActionScopeType, ListActionType } from '@Types';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { THPopConfirm } from '@Molecules/Inputs/THPopConfirm/THPopConfirm';

import './ActionList.scss';

interface ActionListProps {
    showLabel?: boolean;
    actions: { [key: string]: ListActionType };
    scope?: ListActionScopeType;
    hidden?: boolean;
}

const ActionList = ({ showLabel, actions, scope = 'both', hidden = false }: ActionListProps): ReactElement | null => {
    const i = useLocalMessage();

    return !hidden ? (
        <div className="action-list">
            {Object.entries(actions).map(
                ([
                    key,
                    {
                        label,
                        message,
                        query,
                        icon,
                        disabled = false,
                        confirm = false,
                        hidden = false,
                        CustomComponent,
                        scope: actionScope = 'both',
                    },
                ]) => {
                    const hideAction = hidden || (actionScope !== scope && actionScope !== 'both');

                    if (hideAction) {
                        return null;
                    }

                    const handleConfirm = () => {
                        query?.();
                    };

                    const Action = (
                        <THTooltip title={label} placement="bottom" key={label}>
                            <THButton type="default" icon={icon} disabled={disabled} {...{ 'data-testid': key }}>
                                {(!icon || showLabel) && label}
                            </THButton>
                        </THTooltip>
                    );

                    if (CustomComponent) {
                        return React.cloneElement(CustomComponent(Action), { key: label });
                    }

                    if (confirm) {
                        return (
                            <THPopConfirm
                                key={label}
                                title={message}
                                onConfirm={handleConfirm}
                                okText={i('general.ok')}
                                cancelText={i('general.cancel')}
                            >
                                {Action}
                            </THPopConfirm>
                        );
                    }

                    return React.cloneElement(Action, { onClick: handleConfirm });
                },
            )}
        </div>
    ) : null;
};

export default ActionList;
